import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, PlayCircle, XCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { formatNumber } from '@/utils/formatNumber';
import { toast } from 'sonner';

interface ActiveJob {
  id: string;
  job_type: string;
  status: string;
  total_photos: number;
  processed: number;
  successful: number;
  failed: number;
  created_at: string;
}

interface ActiveJobsProps {
  organizationId: string;
  viewMode?: 'desktop' | 'icon';
}

export function ActiveJobs({ organizationId, viewMode = 'desktop' }: ActiveJobsProps) {
  const [jobs, setJobs] = useState<ActiveJob[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organizationId) return;

    const fetchJobs = async () => {
      const { data, error } = await supabase
        .from('reprocessing_jobs')
        .select('*')
        .eq('organization_id', organizationId)
        .in('status', ['pending', 'running'])
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching jobs:', error);
      } else {
        setJobs(data || []);
      }
      setLoading(false);
    };

    fetchJobs();

    // Subscribe to realtime updates
    const channel = supabase
      .channel(`reprocessing_jobs:${organizationId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reprocessing_jobs',
          filter: `organization_id=eq.${organizationId}`
        },
        (payload) => {
          console.log('[ActiveJobs] Realtime update:', payload);
          
          if (payload.eventType === 'INSERT') {
            const newJob = payload.new as ActiveJob;
            if (newJob.status === 'pending' || newJob.status === 'running') {
              setJobs(prev => [newJob, ...prev]);
            }
          } else if (payload.eventType === 'UPDATE') {
            const updatedJob = payload.new as ActiveJob;
            
            if (updatedJob.status === 'complete' || updatedJob.status === 'stopped' || updatedJob.status === 'failed') {
              // Remove completed jobs
              setJobs(prev => prev.filter(j => j.id !== updatedJob.id));
            } else {
              // Update job progress
              setJobs(prev => prev.map(j => j.id === updatedJob.id ? updatedJob : j));
            }
          } else if (payload.eventType === 'DELETE') {
            setJobs(prev => prev.filter(j => j.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [organizationId]);

  const handleStopJob = async (jobId: string) => {
    try {
      const { error } = await supabase.functions.invoke('stop-job', {
        body: { jobId }
      });

      if (error) {
        toast.error('Failed to stop job');
      } else {
        toast.info('Job stop requested');
      }
    } catch (error) {
      console.error('Error stopping job:', error);
      toast.error('Failed to stop job');
    }
  };

  if (loading) {
    return null;
  }

  if (jobs.length === 0) {
    return null;
  }

  const getJobTypeLabel = (jobType: string) => {
    switch (jobType) {
      case 'no-tags': return 'No Tags';
      case 'failed': return 'Failed Photos';
      case 'all': return 'All Photos';
      case 'pii': return 'PII Detection';
      default: return jobType;
    }
  };

  return (
    <div className="mb-4">
      <div className={`px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider ${viewMode === 'icon' ? 'hidden' : 'lg:block md:hidden'}`}>
        Jobs Running
      </div>
      
      <div className="space-y-2">
        {jobs.map(job => {
          const percent = job.total_photos > 0 
            ? Math.min(100, (job.processed / job.total_photos) * 100) 
            : 0;

          return (
            <div
              key={job.id}
              className="p-3 rounded-md bg-secondary/30 border border-border"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <span className="text-sm font-medium lg:block md:hidden">
                    {getJobTypeLabel(job.job_type)}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleStopJob(job.id)}
                  className="h-6 px-2 lg:flex md:hidden"
                >
                  <XCircle className="h-3 w-3 mr-1" />
                  Stop
                </Button>
              </div>

              <div className="space-y-1 lg:block md:hidden">
                <Progress value={percent} className="h-1" />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{formatNumber(job.processed)} / {formatNumber(job.total_photos)}</span>
                  <span>{Math.round(percent)}%</span>
                </div>
                {job.failed > 0 && (
                  <div className="text-xs text-destructive">
                    {formatNumber(job.failed)} failed
                  </div>
                )}
              </div>

              {/* Mobile/Icon view - just show icon */}
              <div className="lg:hidden md:block text-center">
                <PlayCircle className="h-5 w-5 mx-auto text-primary" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
